/**
 * Field-diagnostics trap. Imported FIRST from main.ts so its listeners are
 * armed before anything else in the bundle can fail.
 *
 * Silent in normal use. Open the site with `#debug` in the URL
 * (https://meditationcompass.au/#debug) and it paints a monospace banner
 * that reports, live, on the actual device:
 *
 *   - a "trap armed" line (proves the fresh bundle is what loaded)
 *   - every uncaught error and unhandled promise rejection
 *   - every history.pushState/replaceState the app performs
 *
 * This exists because the launch-critical bugs are iOS-only and prod has
 * no Sentry DSN yet — the tester's phone IS the console. Remove (or leave;
 * it is inert without the hash) once real telemetry lands.
 */

const ARMED: boolean =
  typeof location !== "undefined" && location.hash.includes("debug");

let panel: HTMLElement | null = null;

const ensurePanel = (): HTMLElement | null => {
  if (typeof document === "undefined" || document.body === null) return null;
  if (panel !== null) return panel;
  panel = document.createElement("div");
  panel.style.cssText = [
    "position:fixed",
    "top:0",
    "left:0",
    "right:0",
    "z-index:2147483647",
    "background:rgba(96,8,20,0.93)",
    "color:#ffe9e9",
    "font:11px/1.45 ui-monospace,monospace",
    "padding:6px 9px calc(6px + env(safe-area-inset-top)) 9px",
    "max-height:45vh",
    "overflow:auto",
    "white-space:pre-wrap",
    "word-break:break-all",
    "pointer-events:none",
  ].join(";");
  document.body.appendChild(panel);
  return panel;
};

const line = (text: string): void => {
  if (!ARMED) return;
  const p = ensurePanel();
  if (p === null) return;
  const row = document.createElement("div");
  const t = new Date();
  const hh = String(t.getHours()).padStart(2, "0");
  const mm = String(t.getMinutes()).padStart(2, "0");
  const ss = String(t.getSeconds()).padStart(2, "0");
  row.textContent = `${hh}:${mm}:${ss} ${text}`;
  p.appendChild(row);
  p.scrollTop = p.scrollHeight;
};

if (typeof window !== "undefined") {
  window.addEventListener("error", (e: ErrorEvent) => {
    line(`[error] ${e.message} @ ${e.filename ?? "?"}:${e.lineno ?? 0}`);
  });
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const reason: unknown = e.reason;
    const msg =
      reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason);
    line(`[promise] ${msg}`);
  });

  // Surface the app's own navigations — Elm's Nav.pushUrl lands here.
  // Seeing "[nav] /app/play/love" after a dial tap proves the 4s charge
  // completed and the failure is downstream (render); its absence proves
  // the charge/timer/update loop died.
  const wrapHistory = (method: "pushState" | "replaceState"): void => {
    const original = history[method].bind(history);
    history[method] = ((data: unknown, unused: string, url?: string | URL | null) => {
      line(`[${method === "pushState" ? "nav" : "nav*"}] ${String(url ?? "?")}`);
      return original(data, unused, url);
    }) as typeof history.pushState;
  };
  wrapHistory("pushState");
  wrapHistory("replaceState");

  if (ARMED) {
    // Defer the armed line until the DOM can hold the panel.
    const announce = (): void => {
      line(`[trap] armed — build ${import.meta.env.MODE}`);
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", announce, { once: true });
    } else {
      announce();
    }
  }
}

export {};
